import { useState, useEffect } from "react";
import API from "../../api/api";
import {
  Settings as SettingsIcon,
  Mail,
  Lock,
  KeyRound,
  ShieldCheck,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Send,
  Eye,
  EyeOff,
  RefreshCcw,
  Check,
  Copy,
  Coins,
  Video,
  PhoneCall,
  Radio,
  Users as UsersGroup,
  MessageSquare,
  Save,
} from "lucide-react";

export default function Settings() {
  const [profile, setProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [activeTab, setActiveTab] = useState("pricing"); // "pricing" | "email" | "password"

  // Feedback states
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [devOtpNotification, setDevOtpNotification] = useState(null);
  const [copiedOtp, setCopiedOtp] = useState(false);

  // Platform Pricing State
  const [pricingForm, setPricingForm] = useState({
    chatPricePerEntry: 5,
    videoCallPricePerMinute: 20,
    audioCallPricePerMinute: 10,
    liveStreamEntryPrice: 10,
    roomEntryFees: 10,
  });
  const [loadingPricing, setLoadingPricing] = useState(false);
  const [savingPricing, setSavingPricing] = useState(false);

  // Email update state
  const [newEmail, setNewEmail] = useState("");
  const [emailOtp, setEmailOtp] = useState("");
  const [emailOtpSent, setEmailOtpSent] = useState(false);
  const [sendingEmailOtp, setSendingEmailOtp] = useState(false);
  const [updatingEmail, setUpdatingEmail] = useState(false);
  const [emailTimer, setEmailTimer] = useState(0);

  // Password update state
  const [passwordOtp, setPasswordOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [passwordOtpSent, setPasswordOtpSent] = useState(false);
  const [sendingPasswordOtp, setSendingPasswordOtp] = useState(false);
  const [updatingPassword, setUpdatingPassword] = useState(false);
  const [passwordTimer, setPasswordTimer] = useState(0);

  // Fetch admin profile
  const fetchProfile = async () => {
    try {
      setLoadingProfile(true);
      setErrorMsg("");
      const { data } = await API.get("/admin/settings/profile");
      if (data?.success) {
        setProfile(data.admin);
      }
    } catch (err) {
      setErrorMsg(err?.response?.data?.message || err.message || "Failed to load admin profile");
    } finally {
      setLoadingProfile(false);
    }
  };

  // Fetch platform pricing settings
  const fetchPricing = async () => {
    try {
      setLoadingPricing(true);
      const { data } = await API.get("/admin/settings/pricing");
      if (data?.success && data.pricing) {
        setPricingForm({
          chatPricePerEntry: data.pricing.chatPricePerEntry ?? 5,
          videoCallPricePerMinute: data.pricing.videoCallPricePerMinute ?? 20,
          audioCallPricePerMinute: data.pricing.audioCallPricePerMinute ?? 10,
          liveStreamEntryPrice: data.pricing.liveStreamEntryPrice ?? 10,
          roomEntryFees: data.pricing.roomEntryFees ?? 10,
        });
      }
    } catch (err) {
      console.error("Failed to load platform pricing:", err);
    } finally {
      setLoadingPricing(false);
    }
  };

  useEffect(() => {
    fetchProfile();
    fetchPricing();
  }, []);

  // Countdown timers for OTP resend
  useEffect(() => {
    let interval = null;
    if (emailTimer > 0) {
      interval = setInterval(() => setEmailTimer((prev) => prev - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [emailTimer]);

  useEffect(() => {
    let interval = null;
    if (passwordTimer > 0) {
      interval = setInterval(() => setPasswordTimer((prev) => prev - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [passwordTimer]);

  // Handle Save Platform Pricing
  const handleSavePricing = async (e) => {
    e.preventDefault();
    try {
      setSavingPricing(true);
      setErrorMsg("");
      setSuccessMsg("");

      const { data } = await API.put("/admin/settings/pricing", pricingForm);
      if (data?.success) {
        setSuccessMsg("Platform pricing & entry fees saved successfully!");
        if (data.pricing) {
          setPricingForm({
            chatPricePerEntry: data.pricing.chatPricePerEntry ?? 5,
            videoCallPricePerMinute: data.pricing.videoCallPricePerMinute ?? 20,
            audioCallPricePerMinute: data.pricing.audioCallPricePerMinute ?? 10,
            liveStreamEntryPrice: data.pricing.liveStreamEntryPrice ?? 10,
            roomEntryFees: data.pricing.roomEntryFees ?? 10,
          });
        }
      }
    } catch (err) {
      setErrorMsg(err?.response?.data?.message || err.message || "Failed to update platform pricing");
    } finally {
      setSavingPricing(false);
    }
  };

  // Handle Send Email Update OTP
  const handleSendEmailOtp = async (e) => {
    e.preventDefault();
    if (!newEmail.trim()) {
      setErrorMsg("Please enter a new email address.");
      return;
    }

    try {
      setSendingEmailOtp(true);
      setErrorMsg("");
      setSuccessMsg("");
      setDevOtpNotification(null);

      const { data } = await API.post("/admin/settings/send-email-otp", {
        newEmail: newEmail.trim(),
      });

      if (!data?.success) throw new Error(data?.message || "Failed to send OTP");

      setSuccessMsg(data.message || `Verification OTP sent to ${newEmail}`);
      setEmailOtpSent(true);
      setEmailTimer(60); // 60s cooldown

      if (data?.devOtp) {
        setDevOtpNotification(data.devOtp);
        setEmailOtp(data.devOtp);
      }
    } catch (err) {
      setErrorMsg(err?.response?.data?.message || err.message || "Error sending verification code");
    } finally {
      setSendingEmailOtp(false);
    }
  };

  // Handle Verify & Update Email
  const handleVerifyEmail = async (e) => {
    e.preventDefault();
    if (!emailOtp.trim()) {
      setErrorMsg("Please enter the 6-digit verification OTP.");
      return;
    }

    try {
      setUpdatingEmail(true);
      setErrorMsg("");
      setSuccessMsg("");

      const { data } = await API.post("/admin/settings/update-email", {
        otp: emailOtp.trim(),
      });

      if (!data?.success) throw new Error(data?.message || "Failed to update email");

      setSuccessMsg(`Admin email updated successfully to ${data.email || newEmail}!`);
      setNewEmail("");
      setEmailOtp("");
      setEmailOtpSent(false);
      setDevOtpNotification(null);
      fetchProfile();
    } catch (err) {
      setErrorMsg(err?.response?.data?.message || err.message || "Error verifying OTP");
    } finally {
      setUpdatingEmail(false);
    }
  };

  // Handle Send Password Change OTP
  const handleSendPasswordOtp = async () => {
    try {
      setSendingPasswordOtp(true);
      setErrorMsg("");
      setSuccessMsg("");
      setDevOtpNotification(null);

      const { data } = await API.post("/admin/settings/send-password-otp");
      if (!data?.success) throw new Error(data?.message || "Failed to send security OTP");

      setSuccessMsg(data.message || "Security OTP sent to your admin email address.");
      setPasswordOtpSent(true);
      setPasswordTimer(60);

      if (data?.devOtp) {
        setDevOtpNotification(data.devOtp);
        setPasswordOtp(data.devOtp);
      }
    } catch (err) {
      setErrorMsg(err?.response?.data?.message || err.message || "Error sending security OTP");
    } finally {
      setSendingPasswordOtp(false);
    }
  };

  // Handle Verify & Update Password
  const handleVerifyPassword = async (e) => {
    e.preventDefault();
    if (!passwordOtp.trim()) {
      setErrorMsg("Please enter the security OTP sent to your email.");
      return;
    }

    if (!newPassword || newPassword.length < 6) {
      setErrorMsg("New password must be at least 6 characters long.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setErrorMsg("New password and confirm password do not match.");
      return;
    }

    try {
      setUpdatingPassword(true);
      setErrorMsg("");
      setSuccessMsg("");

      const { data } = await API.post("/admin/settings/update-password", {
        otp: passwordOtp.trim(),
        newPassword,
        confirmPassword,
      });

      if (!data?.success) throw new Error(data?.message || "Failed to update password");

      setSuccessMsg("Admin password updated successfully! Please use your new password next time you login.");
      setPasswordOtp("");
      setNewPassword("");
      setConfirmPassword("");
      setPasswordOtpSent(false);
      setDevOtpNotification(null);
    } catch (err) {
      setErrorMsg(err?.response?.data?.message || err.message || "Error updating password");
    } finally {
      setUpdatingPassword(false);
    }
  };

  const copyDevOtp = () => {
    if (devOtpNotification) {
      navigator.clipboard.writeText(devOtpNotification);
      setCopiedOtp(true);
      setTimeout(() => setCopiedOtp(false), 2000);
    }
  };

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      {/* Top Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <SettingsIcon className="w-7 h-7 text-indigo-500" /> Platform & Account Settings
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Manage global coin pricing, entry fees, credentials, and security configurations.
          </p>
        </div>
        <button
          onClick={() => {
            fetchProfile();
            fetchPricing();
          }}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-700 text-sm font-medium transition-all shadow-sm self-start md:self-auto"
        >
          <RefreshCcw className={`w-4 h-4 ${loadingProfile || loadingPricing ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {/* Global Alerts */}
      {successMsg && (
        <div className="p-4 rounded-2xl bg-green-50 dark:bg-green-500/10 border border-green-200 dark:border-green-500/20 text-green-700 dark:text-green-300 flex items-start gap-3 animate-fadeIn">
          <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div className="text-sm font-medium">{successMsg}</div>
        </div>
      )}

      {errorMsg && (
        <div className="p-4 rounded-2xl bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 text-red-700 dark:text-red-300 flex items-start gap-3 animate-fadeIn">
          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div className="text-sm font-medium whitespace-pre-wrap">{errorMsg}</div>
        </div>
      )}

      {/* Dev Fallback OTP Card */}
      {devOtpNotification && (
        <div className="p-4 rounded-2xl bg-amber-50 dark:bg-amber-500/10 border border-amber-300 dark:border-amber-500/30 text-amber-900 dark:text-amber-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3 animate-fadeIn">
          <div className="flex items-center gap-3">
            <span className="px-2.5 py-1 rounded-lg bg-amber-200 dark:bg-amber-500/30 text-xs font-bold uppercase tracking-wider">
              OTP Code
            </span>
            <span className="text-sm">
              Your 6-Digit OTP Code is: <strong className="font-mono text-lg font-bold tracking-widest text-amber-700 dark:text-amber-300">{devOtpNotification}</strong>
            </span>
          </div>
          <button
            type="button"
            onClick={copyDevOtp}
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg bg-white dark:bg-slate-800 border border-amber-300 dark:border-amber-600 text-xs font-semibold text-amber-800 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-slate-700 transition-all self-start sm:self-auto"
          >
            {copiedOtp ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
            {copiedOtp ? "Copied!" : "Copy OTP"}
          </button>
        </div>
      )}

      {/* Admin Profile Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Current Admin */}
        <div className="p-5 rounded-2xl bg-gradient-to-br from-indigo-500/10 to-purple-500/10 border border-indigo-500/20">
          <p className="text-xs font-bold uppercase tracking-wider text-indigo-600 dark:text-indigo-400">
            Current Admin Account
          </p>
          <h4 className="text-lg font-bold text-gray-900 dark:text-white mt-1">
            {profile?.name || "Admin"}
          </h4>
          <p className="text-xs font-mono text-gray-500 dark:text-gray-400 truncate mt-0.5">
            {profile?.email || "loading..."}
          </p>
          <span className="inline-block mt-3 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300">
            Role: {profile?.role || "superadmin"}
          </span>
        </div>

        {/* Pricing Summary */}
        <div className="p-5 rounded-2xl bg-gradient-to-br from-amber-500/10 to-orange-500/10 border border-amber-500/20">
          <p className="text-xs font-bold uppercase tracking-wider text-amber-600 dark:text-amber-400">
            Default Platform Fees
          </p>
          <h4 className="text-lg font-bold text-gray-900 dark:text-white mt-1 flex items-center gap-1.5">
            <Coins className="w-5 h-5 text-amber-500" /> Video: {pricingForm.videoCallPricePerMinute}c • Audio: {pricingForm.audioCallPricePerMinute}c/min
          </h4>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Chat: {pricingForm.chatPricePerEntry}c • Live: {pricingForm.liveStreamEntryPrice}c • Room: {pricingForm.roomEntryFees}c
          </p>
          <span className="inline-block mt-3 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300">
            Auto Synced with Users
          </span>
        </div>

        {/* Security Info Card */}
        <div className="p-5 rounded-2xl bg-gradient-to-br from-emerald-500/10 to-teal-500/10 border border-emerald-500/20">
          <p className="text-xs font-bold uppercase tracking-wider text-emerald-600 dark:text-emerald-400">
            Security Protection
          </p>
          <h4 className="text-lg font-bold text-gray-900 dark:text-white mt-1">
            2-Factor OTP Auth
          </h4>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Email & password changes verified via time-limited 6-digit OTP codes.
          </p>
          <span className="inline-block mt-3 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300">
            10-min OTP Expiry
          </span>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="flex items-center gap-2 p-1.5 rounded-2xl bg-gray-100 dark:bg-slate-800/80 border border-gray-200 dark:border-slate-700">
        <button
          onClick={() => {
            setActiveTab("pricing");
            setErrorMsg("");
            setSuccessMsg("");
            setDevOtpNotification(null);
          }}
          className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold transition-all ${
            activeTab === "pricing"
              ? "bg-white dark:bg-slate-700 text-amber-600 dark:text-amber-300 shadow-sm"
              : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
          }`}
        >
          <Coins className="w-4 h-4" />
          Platform Fees & Pricing
        </button>

        <button
          onClick={() => {
            setActiveTab("email");
            setErrorMsg("");
            setSuccessMsg("");
            setDevOtpNotification(null);
          }}
          className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold transition-all ${
            activeTab === "email"
              ? "bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-300 shadow-sm"
              : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
          }`}
        >
          <Mail className="w-4 h-4" />
          Update Admin Email
        </button>

        <button
          onClick={() => {
            setActiveTab("password");
            setErrorMsg("");
            setSuccessMsg("");
            setDevOtpNotification(null);
          }}
          className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold transition-all ${
            activeTab === "password"
              ? "bg-white dark:bg-slate-700 text-purple-600 dark:text-purple-300 shadow-sm"
              : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
          }`}
        >
          <Lock className="w-4 h-4" />
          Change Password
        </button>
      </div>

      {/* TAB 0: PLATFORM PRICING & ENTRY FEES */}
      {activeTab === "pricing" && (
        <div className="bg-white dark:bg-slate-800 rounded-3xl border border-gray-200 dark:border-slate-700 p-6 md:p-8 space-y-6 shadow-sm animate-fadeIn">
          <div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Coins className="w-5 h-5 text-amber-500" /> Customizable Platform Pricing & Entry Fees
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Configure default coin charges for video calls, audio calls, chat entries, live streams, and party rooms. Users fetch these default values via <code>GET /api/chat/pricing/me</code>.
            </p>
          </div>

          <form onSubmit={handleSavePricing} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* chatPricePerEntry */}
              <div className="p-4 rounded-2xl border border-gray-200 dark:border-slate-700 bg-gray-50/70 dark:bg-slate-900/50 space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-bold text-gray-800 dark:text-gray-200 flex items-center gap-2 uppercase tracking-wider">
                    <MessageSquare className="w-4 h-4 text-blue-500" /> Chat Entry Price
                  </label>
                  <span className="text-[11px] font-mono text-gray-400">chatPricePerEntry</span>
                </div>
                <p className="text-[11px] text-gray-500 dark:text-gray-400">
                  Coins deducted when a user initiates/enters a direct chat conversation.
                </p>
                <div className="relative">
                  <input
                    type="number"
                    min={0}
                    required
                    value={pricingForm.chatPricePerEntry}
                    onChange={(e) => setPricingForm((prev) => ({ ...prev, chatPricePerEntry: Number(e.target.value) }))}
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-white text-sm font-semibold focus:ring-2 focus:ring-amber-500 focus:outline-none"
                  />
                  <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-xs font-bold text-gray-400">Coins</span>
                </div>
              </div>

              {/* videoCallPricePerMinute */}
              <div className="p-4 rounded-2xl border border-gray-200 dark:border-slate-700 bg-gray-50/70 dark:bg-slate-900/50 space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-bold text-gray-800 dark:text-gray-200 flex items-center gap-2 uppercase tracking-wider">
                    <Video className="w-4 h-4 text-emerald-500" /> Video Call Price / Min
                  </label>
                  <span className="text-[11px] font-mono text-gray-400">videoCallPricePerMinute</span>
                </div>
                <p className="text-[11px] text-gray-500 dark:text-gray-400">
                  Coins deducted per minute during 1-on-1 private video calls.
                </p>
                <div className="relative">
                  <input
                    type="number"
                    min={0}
                    required
                    value={pricingForm.videoCallPricePerMinute}
                    onChange={(e) => setPricingForm((prev) => ({ ...prev, videoCallPricePerMinute: Number(e.target.value) }))}
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-white text-sm font-semibold focus:ring-2 focus:ring-amber-500 focus:outline-none"
                  />
                  <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-xs font-bold text-gray-400">Coins/min</span>
                </div>
              </div>

              {/* audioCallPricePerMinute */}
              <div className="p-4 rounded-2xl border border-gray-200 dark:border-slate-700 bg-gray-50/70 dark:bg-slate-900/50 space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-bold text-gray-800 dark:text-gray-200 flex items-center gap-2 uppercase tracking-wider">
                    <PhoneCall className="w-4 h-4 text-cyan-500" /> Audio Call Price / Min
                  </label>
                  <span className="text-[11px] font-mono text-gray-400">audioCallPricePerMinute</span>
                </div>
                <p className="text-[11px] text-gray-500 dark:text-gray-400">
                  Coins deducted per minute during 1-on-1 private audio calls.
                </p>
                <div className="relative">
                  <input
                    type="number"
                    min={0}
                    required
                    value={pricingForm.audioCallPricePerMinute}
                    onChange={(e) => setPricingForm((prev) => ({ ...prev, audioCallPricePerMinute: Number(e.target.value) }))}
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-white text-sm font-semibold focus:ring-2 focus:ring-amber-500 focus:outline-none"
                  />
                  <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-xs font-bold text-gray-400">Coins/min</span>
                </div>
              </div>

              {/* liveStreamEntryPrice */}
              <div className="p-4 rounded-2xl border border-gray-200 dark:border-slate-700 bg-gray-50/70 dark:bg-slate-900/50 space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-bold text-gray-800 dark:text-gray-200 flex items-center gap-2 uppercase tracking-wider">
                    <Radio className="w-4 h-4 text-rose-500" /> Live Stream Entry Fee
                  </label>
                  <span className="text-[11px] font-mono text-gray-400">liveStreamEntryPrice</span>
                </div>
                <p className="text-[11px] text-gray-500 dark:text-gray-400">
                  Coins required to join and view a paid live broadcast.
                </p>
                <div className="relative">
                  <input
                    type="number"
                    min={0}
                    required
                    value={pricingForm.liveStreamEntryPrice}
                    onChange={(e) => setPricingForm((prev) => ({ ...prev, liveStreamEntryPrice: Number(e.target.value) }))}
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-white text-sm font-semibold focus:ring-2 focus:ring-amber-500 focus:outline-none"
                  />
                  <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-xs font-bold text-gray-400">Coins</span>
                </div>
              </div>

              {/* roomEntryFees */}
              <div className="p-4 rounded-2xl border border-gray-200 dark:border-slate-700 bg-gray-50/70 dark:bg-slate-900/50 space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-bold text-gray-800 dark:text-gray-200 flex items-center gap-2 uppercase tracking-wider">
                    <UsersGroup className="w-4 h-4 text-purple-500" /> Room Entry Fees
                  </label>
                  <span className="text-[11px] font-mono text-gray-400">roomEntryFees</span>
                </div>
                <p className="text-[11px] text-gray-500 dark:text-gray-400">
                  Coins required for viewers to enter paid audio/chat party rooms.
                </p>
                <div className="relative">
                  <input
                    type="number"
                    min={0}
                    required
                    value={pricingForm.roomEntryFees}
                    onChange={(e) => setPricingForm((prev) => ({ ...prev, roomEntryFees: Number(e.target.value) }))}
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-white text-sm font-semibold focus:ring-2 focus:ring-amber-500 focus:outline-none"
                  />
                  <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-xs font-bold text-gray-400">Coins</span>
                </div>
              </div>
            </div>

            {/* Save Button */}
            <div className="pt-3">
              <button
                type="submit"
                disabled={savingPricing}
                className="px-8 py-3.5 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm font-bold hover:opacity-90 disabled:opacity-50 flex items-center gap-2 transition-all shadow-md shadow-amber-500/20"
              >
                {savingPricing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save Platform Pricing & Fees
              </button>
            </div>
          </form>
        </div>
      )}

      {/* TAB 1: UPDATE EMAIL */}
      {activeTab === "email" && (
        <div className="bg-white dark:bg-slate-800 rounded-3xl border border-gray-200 dark:border-slate-700 p-6 md:p-8 space-y-6 shadow-sm animate-fadeIn">
          <div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Mail className="w-5 h-5 text-indigo-500" /> Update Admin Email Address
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              A 6-digit verification code will be sent to the new email address.
            </p>
          </div>

          <form onSubmit={!emailOtpSent ? handleSendEmailOtp : handleVerifyEmail} className="space-y-5 max-w-lg">
            {/* Current Email Display */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                Current Registered Email
              </label>
              <input
                type="text"
                disabled
                value={profile?.email || ""}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-slate-700 bg-gray-100 dark:bg-slate-900 text-gray-500 dark:text-gray-400 text-sm font-mono cursor-not-allowed"
              />
            </div>

            {/* New Email Input */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                New Email Address
              </label>
              <input
                type="email"
                required
                disabled={emailOtpSent}
                placeholder="admin-new@example.com"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none disabled:opacity-60"
              />
            </div>

            {/* If OTP Sent -> Show OTP Input */}
            {emailOtpSent && (
              <div className="p-4 rounded-2xl bg-indigo-50/60 dark:bg-indigo-500/10 border border-indigo-200 dark:border-indigo-500/30 space-y-3 animate-slideDown">
                <label className="text-xs font-bold text-indigo-700 dark:text-indigo-300 uppercase tracking-wider block">
                  Enter 6-Digit Email Verification Code
                </label>
                <input
                  type="text"
                  maxLength={6}
                  required
                  placeholder="e.g. 123456"
                  value={emailOtp}
                  onChange={(e) => setEmailOtp(e.target.value.replace(/\D/g, ""))}
                  className="w-full px-4 py-3 text-center tracking-[8px] text-xl font-bold rounded-xl border border-indigo-300 dark:border-indigo-500 bg-white dark:bg-slate-900 text-indigo-900 dark:text-indigo-200 focus:ring-2 focus:ring-indigo-500 focus:outline-none font-mono"
                />
                <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 pt-1">
                  <span>Didn't receive the code?</span>
                  <button
                    type="button"
                    onClick={handleSendEmailOtp}
                    disabled={emailTimer > 0 || sendingEmailOtp}
                    className="text-indigo-600 dark:text-indigo-400 font-bold hover:underline disabled:opacity-50"
                  >
                    {emailTimer > 0 ? `Resend code in ${emailTimer}s` : "Resend Code"}
                  </button>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="pt-2 flex items-center gap-3">
              {!emailOtpSent ? (
                <button
                  type="submit"
                  disabled={sendingEmailOtp || !newEmail.trim()}
                  className="px-6 py-3 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50 flex items-center gap-2 transition-all shadow-md shadow-indigo-500/20"
                >
                  {sendingEmailOtp ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  Send Verification Code via SMTP
                </button>
              ) : (
                <>
                  <button
                    type="submit"
                    disabled={updatingEmail || !emailOtp.trim()}
                    className="px-6 py-3 rounded-xl bg-gradient-to-r from-emerald-600 to-green-600 text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50 flex items-center gap-2 transition-all shadow-md shadow-emerald-500/20"
                  >
                    {updatingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                    Verify & Update Email
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setEmailOtpSent(false);
                      setEmailOtp("");
                    }}
                    className="px-4 py-3 rounded-xl border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-300 text-sm font-medium hover:bg-gray-100 dark:hover:bg-slate-700"
                  >
                    Cancel
                  </button>
                </>
              )}
            </div>
          </form>
        </div>
      )}

      {/* TAB 2: CHANGE PASSWORD */}
      {activeTab === "password" && (
        <div className="bg-white dark:bg-slate-800 rounded-3xl border border-gray-200 dark:border-slate-700 p-6 md:p-8 space-y-6 shadow-sm animate-fadeIn">
          <div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-purple-500" /> Change Admin Password
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              To change your password, request a security OTP to your registered email ({profile?.email}).
            </p>
          </div>

          {!passwordOtpSent ? (
            <div className="p-6 rounded-2xl bg-purple-50/60 dark:bg-purple-500/10 border border-purple-200 dark:border-purple-500/30 max-w-lg space-y-4">
              <div className="flex items-center gap-3 text-purple-900 dark:text-purple-200">
                <ShieldCheck className="w-6 h-6 text-purple-600 dark:text-purple-400 flex-shrink-0" />
                <p className="text-sm font-medium">
                  Click below to receive a 6-digit security authorization code.
                </p>
              </div>

              <button
                type="button"
                onClick={handleSendPasswordOtp}
                disabled={sendingPasswordOtp}
                className="w-full py-3 rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2 transition-all shadow-md shadow-purple-500/20"
              >
                {sendingPasswordOtp ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Send Security OTP to Email
              </button>
            </div>
          ) : (
            <form onSubmit={handleVerifyPassword} className="space-y-5 max-w-lg animate-slideDown">
              <div className="p-3.5 rounded-xl bg-purple-50 dark:bg-purple-500/10 border border-purple-200 dark:border-purple-500/20 text-xs text-purple-800 dark:text-purple-300">
                Security authorization code has been sent to <strong>{profile?.email}</strong>.
              </div>

              {/* OTP Input */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Enter 6-Digit Security OTP
                </label>
                <input
                  type="text"
                  maxLength={6}
                  required
                  placeholder="e.g. 654321"
                  value={passwordOtp}
                  onChange={(e) => setPasswordOtp(e.target.value.replace(/\D/g, ""))}
                  className="w-full px-4 py-3 text-center tracking-[8px] text-xl font-bold rounded-xl border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:outline-none font-mono"
                />
              </div>

              {/* New Password */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  New Password (min 6 characters)
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    required
                    minLength={6}
                    placeholder="Enter new password..."
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-4 py-3 text-sm rounded-xl border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:outline-none pr-11"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Confirm New Password
                </label>
                <input
                  type={showPassword ? "text" : "password"}
                  required
                  minLength={6}
                  placeholder="Re-enter new password..."
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-4 py-3 text-sm rounded-xl border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:outline-none"
                />
              </div>

              {/* Action Buttons */}
              <div className="pt-2 flex items-center gap-3">
                <button
                  type="submit"
                  disabled={updatingPassword || !passwordOtp.trim() || !newPassword}
                  className="px-6 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50 flex items-center gap-2 transition-all shadow-md shadow-purple-500/20"
                >
                  {updatingPassword ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
                  Verify OTP & Save Password
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setPasswordOtpSent(false);
                    setPasswordOtp("");
                  }}
                  className="px-4 py-3 rounded-xl border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-300 text-sm font-medium hover:bg-gray-100 dark:hover:bg-slate-700"
                >
                  Cancel
                </button>
              </div>

              <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 pt-1">
                <span>Didn't receive the code?</span>
                <button
                  type="button"
                  onClick={handleSendPasswordOtp}
                  disabled={passwordTimer > 0 || sendingPasswordOtp}
                  className="text-purple-600 dark:text-purple-400 font-bold hover:underline disabled:opacity-50"
                >
                  {passwordTimer > 0 ? `Resend OTP in ${passwordTimer}s` : "Resend Security OTP"}
                </button>
              </div>
            </form>
          )}
        </div>
      )}
    </div>
  );
}
